import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { EMPTY_TEACHER_EXTENSIONS } from '../../services/TeacherAccessService';
import {
  DEFAULT_TEACHER_RATES,
  TEACHER_RATE_TIERS,
  updateTeacherRate,
} from './teacherRates';

export default function TeacherExtensionsFields({ value, onChange, disabled, idPrefix }) {
  const { t } = useTranslation();
  const settings = { ...EMPTY_TEACHER_EXTENSIONS, ...value };
  const change = (field, next) => onChange({ ...settings, [field]: next });

  const getTierLabel = count => {
    switch (count) {
      case 1: return t('manage:teacherExtensions.personOne');
      case 2: return t('manage:teacherExtensions.personTwo');
      case 3: return t('manage:teacherExtensions.personThree');
      case 4: return t('manage:teacherExtensions.personFour');
      case 5: return t('manage:teacherExtensions.personFive');
      default: return t('manage:teacherExtensions.personSixPlus');
    }
  };

  const handleRateInput = (count, duration, rawValue) => {
    const nextRates = updateTeacherRate(settings.rates, count, duration, rawValue);
    change('rates', nextRates);
  };

  const getDisplayRate = (count, duration) => {
    if (settings.rates && settings.rates[count] && settings.rates[count][duration] !== undefined) {
      return settings.rates[count][duration];
    }
    return DEFAULT_TEACHER_RATES[count][duration];
  };

  return (
    <fieldset disabled={disabled} className="mt-3">
      <legend className="sr-only">{t('manage:teacherExtensions.title')}</legend>
      {['studentsReadAll', 'clientsReadAll'].map(field => (
        <label key={field} className="d-block" htmlFor={`${idPrefix}-extension-${field}`}>
          <input id={`${idPrefix}-extension-${field}`} type="checkbox" checked={settings[field]} onChange={event => change(field, event.target.checked)} />{' '}
          {t(`manage:teacherExtensions.${field}`)}
        </label>
      ))}
      {['classesManageScope'].map(field => (
        <label key={field} className="d-block" htmlFor={`${idPrefix}-extension-${field}`}>
          {t(`manage:teacherExtensions.${field}`)}
          <select id={`${idPrefix}-extension-${field}`} className="form-control" value={settings[field]} onChange={event => change(field, event.target.value)}>
            {['none', 'own', 'school'].map(scope => <option key={scope} value={scope}>{t(`manage:teacherExtensions.${scope}`)}</option>)}
          </select>
        </label>
      ))}
      <small className="d-block text-muted mb-3">{t('manage:teacherExtensions.explanation')}</small>

      <details className="teacher-rates-matrix border-top pt-3">
        <summary className="font-weight-bold mb-2">{t('manage:teacherExtensions.ratesTitle')}</summary>
        <small className="d-block text-muted mb-2">{t('manage:teacherExtensions.ratesExplanation')}</small>
        <div className="table-responsive">
          <table className="table table-sm table-bordered">
            <thead className="thead-light">
              <tr>
                <th scope="col">{t('manage:teacherExtensions.studentsCount')}</th>
                <th scope="col">{t('manage:teacherExtensions.rateDuration45')}</th>
                <th scope="col">{t('manage:teacherExtensions.rateDuration60')}</th>
              </tr>
            </thead>
            <tbody>
              {TEACHER_RATE_TIERS.map(count => (
                <tr key={count}>
                  <td className="align-middle">{getTierLabel(count)}</td>
                  <td>
                    <input
                      id={`${idPrefix}-rate-${count}-45`}
                      aria-label={t('manage:teacherRateLabel', { count: count === 6 ? '6+' : count, duration: 45 })}
                      type="number"
                      min="0"
                      step="1"
                      className="form-control form-control-sm"
                      value={getDisplayRate(count, 45)}
                      onChange={e => handleRateInput(count, 45, e.target.value)}
                      disabled={disabled}
                    />
                  </td>
                  <td>
                    <input
                      id={`${idPrefix}-rate-${count}-60`}
                      aria-label={t('manage:teacherRateLabel', { count: count === 6 ? '6+' : count, duration: 60 })}
                      type="number"
                      min="0"
                      step="1"
                      className="form-control form-control-sm"
                      value={getDisplayRate(count, 60)}
                      onChange={e => handleRateInput(count, 60, e.target.value)}
                      disabled={disabled}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </fieldset>
  );
}
TeacherExtensionsFields.propTypes = { value: PropTypes.object, onChange: PropTypes.func.isRequired, disabled: PropTypes.bool, idPrefix: PropTypes.string };
TeacherExtensionsFields.defaultProps = { value: EMPTY_TEACHER_EXTENSIONS, disabled: false, idPrefix: 'teacher' };
